<template>
    <div v-if="isError">
        <div class="py-14 text-center px-4 text-gray-500 text-lg">{{ $t('views.monitoring.teamComparison.errors.couldNotLoadData') }}</div>
    </div>
    <GenericDashboard
        v-else-if="dashboard || isFetching"
        :data="dashboard"
        :loading="isFetching"
        :fetch-params="computedFetchState"
        :fetch-action-type="ActionType.GetStudyPeriodsDashboard"
        data-key="tables"
        persistent-loading
        :no-data-text="$t('views.monitoring.teamComparison.texts.noData')"
        @update:item="onUpdateItem"
        @update:section-collapse="onUpdateSectionCollapse"
    />
    <div v-else class="py-14 text-center px-4 text-gray-500 text-lg">{{ $t('views.monitoring.teamComparison.texts.generateToViewInsight') }}</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import GenericDashboard from '@/components/common/dashboard/GenericDashboard.vue';
import { ActionType } from '@/plugins/store/actions';
import { Dashboard, DashboardItem } from '@/types';
import { useStudyPeriodDashboard, StudyPeriodDashboardPayload } from '@/plugins/store/actions/queries/study-periods/dashboards';

interface Props {
    fetchState: (StudyPeriodDashboardPayload & { fetchIndex: number }) | null;
}

const props = defineProps<Props>();

const computedFetchState = computed(() => {
    if (props.fetchState) {
        const { fetchIndex, ...payload } = props.fetchState;
        return payload;
    }

    return null;
});
const isQueryEnabled = computed(() => props.fetchState !== null);

const { data, isFetching, isError, refetch } = useStudyPeriodDashboard(computedFetchState, { enabled: isQueryEnabled });

const dashboard = ref<Dashboard | undefined>();

watch(
    data,
    (newData) => {
        dashboard.value = newData?.tables ? cloneDeep(newData.tables) : undefined;
    },
    { immediate: true }
);

watch(
    () => props.fetchState?.fetchIndex,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            refetch();
        }
    }
);

function onUpdateItem({ item }: { item: DashboardItem }) {
    const itemIndex = dashboard.value!.items.findIndex((i) => i.item_no === item.item_no);

    if (itemIndex !== -1) {
        dashboard.value!.items[itemIndex] = item;
    }
}

function onUpdateSectionCollapse({ sectionIndex, isCollapsed }: { sectionIndex: number; isCollapsed: boolean }) {
    if (dashboard.value?.sections?.[sectionIndex]) {
        dashboard.value.sections[sectionIndex].is_collapsed = isCollapsed;
    }
}
</script>
